import { BrowserRouter, useLocation } from "react-router-dom";
import { useEffect } from "react";
import { AppRoutes } from "./router";
import { I18nextProvider } from "react-i18next";
import i18n from "./i18n";
import ScrollToTop from "./ScrollToTop";
import { useReferralTracking } from "./hooks/useReferralTracking";
import FloatingWhatsAppButton from "./components/FloatingWhatsAppButton";
import StickyPromoBanner from "./components/launch-offer/StickyPromoBanner";
import { initMetaPixel, track, trackCustom, trackPageView, isMetaPixelAllowed } from "./lib/meta-pixel";
import { AdminSessionProvider } from "./hooks/useAdminSession";


function AppInner() {
  useReferralTracking();
  const { pathname } = useLocation();
  const isAdmin = pathname.startsWith("/admin");
  const isOnlineExperience = pathname === "/online-classes-signup" || pathname.startsWith("/online");

  useEffect(() => {
    if (isAdmin) return;
    initMetaPixel();
    trackPageView();
  }, [pathname, isAdmin]);

  // Defence-in-depth: ensure /admin* is never indexed even if a crawler
  // ignores robots.txt. Inject <meta name="robots" content="noindex,nofollow">
  // only while on an admin route, and remove it on navigation away so public
  // pages are never accidentally marked noindex.
  useEffect(() => {
    if (!isAdmin) return;
    const tag = document.createElement("meta");
    tag.name = "robots";
    tag.content = "noindex,nofollow";
    document.head.appendChild(tag);
    return () => {
      tag.parentNode?.removeChild(tag);
    };
  }, [isAdmin]);

  // Delegated click listener for public-site intent signals:
  // - ApplyCTAClick on any anchor whose href targets /apply (covers all
  //   "Apply Now" / "Start Application" CTAs across the public site).
  // - Contact on mailto:, tel:, and wa.me / WhatsApp links (covers the
  //   floating WhatsApp button too — no separate handler on the button,
  //   so there is no double-track).
  useEffect(() => {
    if (isAdmin) return;
    const onClick = (e: MouseEvent) => {
      if (!isMetaPixelAllowed()) return;
      const target = e.target as Element | null;
      if (!target || typeof target.closest !== "function") return;
      const anchor = target.closest("a") as HTMLAnchorElement | null;
      if (!anchor) return;
      const rawHref = anchor.getAttribute("href") || "";
      if (!rawHref) return;

      // Contact channels
      if (rawHref.startsWith("mailto:")) {
        track("Contact", { content_name: "Email" });
        return;
      }
      if (rawHref.startsWith("tel:")) {
        track("Contact", { content_name: "Phone" });
        return;
      }
      if (/(^|\/\/)(wa\.me|api\.whatsapp\.com|chat\.whatsapp\.com)/i.test(rawHref)) {
        track("Contact", { content_name: "WhatsApp" });
        return;
      }

      // Apply CTA — internal links to /apply (Link renders as <a href="/apply…">).
      // Exclude same-page nav from within /apply itself to avoid noise.
      const isApplyHref =
        rawHref === "/apply" ||
        rawHref.startsWith("/apply?") ||
        rawHref.startsWith("/apply#") ||
        rawHref.startsWith("/apply/");
      if (isApplyHref && !pathname.startsWith("/apply")) {
        const ctaLocation =
          anchor.getAttribute("data-cta-location") || pathname || "unknown";
        trackCustom("ApplyCTAClick", {
          cta_location: ctaLocation,
          destination: "/apply",
        });
      }
    };
    document.addEventListener("click", onClick, true);
    return () => document.removeEventListener("click", onClick, true);
  }, [isAdmin, pathname]);

  return (
    <>
      <ScrollToTop />
      {!isAdmin && <StickyPromoBanner />}
      <AdminSessionProvider>
        <AppRoutes />
      </AdminSessionProvider>
      {!isAdmin && !isOnlineExperience && <FloatingWhatsAppButton />}
    </>
  );
}

function App() {
  return (
    <I18nextProvider i18n={i18n}>
      <BrowserRouter basename={__BASE_PATH__}>
        <AppInner />
      </BrowserRouter>
    </I18nextProvider>
  );
}

export default App;
